const FALLBACK_SYMBOL = Symbol.for('cmf.safeLocalStorage');

type PrimitiveStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  clear(): void;
  readonly length: number;
};

class MemoryStorage implements PrimitiveStorage {
  private map: Map<string, string>;

  constructor(seed?: Map<string, string>) {
    this.map = seed ? new Map(seed) : new Map();
  }

  get length(): number {
    return this.map.size;
  }

  getItem(key: string): string | null {
    const value = this.map.get(String(key));
    return typeof value === 'string' ? value : null;
  }

  setItem(key: string, value: string): void {
    this.map.set(String(key), String(value));
  }

  removeItem(key: string): void {
    this.map.delete(String(key));
  }

  clear(): void {
    this.map.clear();
  }

  key(index: number): string | null {
    if (index < 0 || index >= this.map.size) return null;
    const entries = Array.from(this.map.keys());
    return entries[index] ?? null;
  }
}

function detectNativeStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {}

  try {
    const globalLocalStorage = (globalThis as unknown as { localStorage?: Storage }).localStorage;
    if (globalLocalStorage) {
      return globalLocalStorage;
    }
  } catch {}

  return null;
}

function getFallbackStorage(): MemoryStorage {
  const globalObj = globalThis as Record<symbol, any>;
  if (!globalObj[FALLBACK_SYMBOL]) {
    globalObj[FALLBACK_SYMBOL] = new MemoryStorage();
  }
  return globalObj[FALLBACK_SYMBOL] as MemoryStorage;
}

const nativeStorage = detectNativeStorage();
const backingStorage: PrimitiveStorage = nativeStorage ?? getFallbackStorage();

type StorageListener = (change: { type: 'set' | 'remove' | 'clear'; key?: string; value?: string | null }) => void;
const listeners = new Set<StorageListener>();

function notify(change: { type: 'set' | 'remove' | 'clear'; key?: string; value?: string | null }) {
  if (nativeStorage) return;
  listeners.forEach(listener => {
    try { listener(change); } catch {}
  });
}

function safeGetItem(key: string): string | null {
  try {
    const value = backingStorage.getItem(key);
    if (!nativeStorage && process.env.NODE_ENV === 'development') {
      console.debug('[safeLocalStorage] fallback getItem', { key, value });
    }
    return value;
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    backingStorage.setItem(key, value);
  } catch {
    // Best-effort in fallback contexts
  }
  if (!nativeStorage && process.env.NODE_ENV === 'development') {
    console.debug('[safeLocalStorage] fallback setItem', { key, value });
  }
  notify({ type: 'set', key, value });
}

function safeRemoveItem(key: string): void {
  try {
    backingStorage.removeItem(key);
  } catch {}
  notify({ type: 'remove', key });
}

function safeKey(index: number): string | null {
  try {
    return backingStorage.key(index);
  } catch {
    return null;
  }
}

function safeClear(): void {
  try {
    backingStorage.clear();
  } catch {}
  notify({ type: 'clear' });
}

function safeLength(): number {
  try {
    return backingStorage.length;
  } catch {
    return 0;
  }
}

export const safeLocalStorage = {
  getItem: safeGetItem,
  setItem: safeSetItem,
  removeItem: safeRemoveItem,
  key: safeKey,
  clear: safeClear,
  get length() {
    return safeLength();
  },
  get isFallback() {
    return !nativeStorage;
  },
  subscribe(listener: StorageListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
} as const;

export function installMemoryStorageSnapshot(snapshot: Record<string, string>): void {
  if (nativeStorage) return;
  const fallback = getFallbackStorage();
  fallback.clear();
  for (const [key, value] of Object.entries(snapshot)) {
    fallback.setItem(key, value);
  }
}

export type SafeLocalStorage = typeof safeLocalStorage;
